import { useState } from "react";
import { loadHillBests, SCORE_KEY } from "../game/hill";
import { HILL } from "../game/maps";
import { storage } from "../game/storage";
import { PRIMARY, SECONDARY } from "./buttons";
import { LABEL, NOTE, PANEL, TITLE } from "./theme";

// Bomb the Hill (#193 to #195) is the lobby's featured choice (#200): a big
// card first in the lobby, with its art (the street dropping away), your best
// distance and its own Drop in, straight to the top of the hill picking your
// ride (#207). Its file is in `src/hills/`, not `map-ideas/`, so a change to
// it runs CI. Its map file is `maps.ts`'s `HILL`.

export { HILL };

/** The street dropping away from the top: kerbs and lane dashes running to a low horizon, parked cars and houses down the sides. CSS colours only. */
function HillArt() {
  return (
    <svg aria-hidden="true" data-hill-art viewBox="0 0 320 120" preserveAspectRatio="xMidYMid slice" className="h-24 w-full rounded-xl border-2 border-ink bg-aqua-soft sm:h-32 [@media(max-height:500px)]:h-16">
      {/* Sky, then the town far below where the road ends. */}
      <rect x="0" y="0" width="320" height="34" className="fill-highlight-soft" />
      <path d="M0 40V30h14v-6h12v6h10v-9h16v19zM250 40V26h12v-8h14v8h8v-5h18v6h18v13z" className="fill-ink/45" />
      {/* Grass verges either side, falling away with the road. */}
      <path d="M0 40h320v80H0z" className="fill-aqua" />
      {/* The road: wide at your feet, a sliver where it drops over the brow. */}
      <path d="M150 38h20l110 82H40z" className="fill-ink-soft" />
      {/* Sidewalks. */}
      <path d="M146 38h4L40 120H14zM170 38h4l132 82h-26z" className="fill-panel/80" />
      {/* Lane dashes, getting longer as they come closer. */}
      <path d="M159 42h2v4h-2zM158.5 52h3v7h-3zM157.5 68h5v11h-5zM156 92h8v18h-8z" className="fill-highlight" />
      {/* Parked cars on the right kerb, and a cone. */}
      <rect x="178" y="48" width="9" height="6" rx="2" className="fill-danger" />
      <rect x="200" y="66" width="18" height="11" rx="3" className="fill-accent" />
      <rect x="236" y="92" width="30" height="18" rx="4" className="fill-pink" />
      <path d="M124 82l5 12h-10z" className="fill-peach" />
      {/* Houses down the left side. */}
      <path d="M96 44h22v-8l11 -6 11 6v8z" className="fill-panel/90" />
      <path d="M20 64h44v-14l22 -12 22 12v14z" className="fill-panel/90" />
    </svg>
  );
}

/**
 * The featured card: the hill's art, its name, your best and Drop in. Until
 * the tutorial (#201) has been seen, Drop in starts with it; after that,
 * `onTutorial` is its own button on the card. Plain labels only (#225).
 */
export function HillCard({ onDropIn, onTutorial, active }: { onDropIn: () => void; onTutorial?: () => void; active: boolean }) {
  // Read once when the lobby shows, which is the only time a new best can have been set since.
  const [best] = useState(() => Math.floor(loadHillBests(storage())[HILL.id] ?? 0));
  // ...and the best total (#205), beside it.
  const [bestScore] = useState(() => Math.floor(loadHillBests(storage(), SCORE_KEY)[HILL.id] ?? 0));
  return (
    <section
      role="group"
      aria-label={HILL.name}
      data-hill-card
      className={`${PANEL} flex w-full flex-col gap-3 p-3 ${active ? "outline-3 outline-offset-4 outline-accent" : ""}`}
    >
      <HillArt />
      <div className="flex items-end justify-between gap-3">
        <h2 className={`${TITLE} text-2xl sm:text-3xl`}>{HILL.name}</h2>
        <p data-hill-best className="shrink-0 text-right">
          <span className={`${LABEL} block`}>Best</span>
          <span className="block text-sm font-black tabular-nums">
            {best > 0 ? `${best} m${bestScore > 0 ? ` · ${bestScore.toLocaleString("en-US")} pts` : ""}` : "No run yet"}
          </span>
        </p>
      </div>
      <div className="flex gap-2">
        <button type="button" autoFocus data-hill-drop title={HILL.blurb} onClick={onDropIn} className={`${PRIMARY} flex-1 px-6 py-3 text-2xl`}>
          Drop in
        </button>
        {onTutorial && (
          <button type="button" data-hill-tutorial onClick={onTutorial} className={`${SECONDARY} px-3 text-xs`}>
            Tutorial
          </button>
        )}
      </div>
      {!onTutorial && <p className={`${NOTE} text-center`}>First run: tutorial</p>}
    </section>
  );
}
